import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Clock, MapPin, UserCheck } from 'lucide-react';
import { getCoachAssignments } from '../../services/coachAPI';

function parseMmDdYyyy(dateStr) {
  const m = String(dateStr || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return null;
  const mm = Number(m[1]);
  const dd = Number(m[2]);
  const yyyy = Number(m[3]);
  const d = new Date(yyyy, mm - 1, dd);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function formatLongDate(dateStr) {
  const d = parseMmDdYyyy(dateStr);
  if (!d) return dateStr || '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatTime12h(hhmm) {
  const m = String(hhmm || '').match(/^(\d{2}):(\d{2})$/);
  if (!m) return hhmm || '—';
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ampm}`;
}

function getCoachDisplayName(assignment) {
  // Handle multi-coach assignments
  if (assignment.coachIds && Array.isArray(assignment.coachIds) && assignment.coachNames) {
    return assignment.coachNames.join(', ');
  } else if (assignment.coachIds && Array.isArray(assignment.coachIds)) {
    // Fallback: show count if names not available
    return `${assignment.coachIds.length} coach${assignment.coachIds.length !== 1 ? 'es' : ''}`;
  }
  // Legacy single-coach assignments
  return assignment.coachName || '—';
}

const Assignments = ({ coachData }) => {
  const coachId = coachData?.id;
  const coachName = `${coachData?.firstName || ''} ${coachData?.lastName || ''}`.trim() || 'Coach';

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getCoachAssignments(coachId);
        if (!alive) return;
        console.log('Coach Assignments - Raw data:', data);
        console.log('Coach Assignments - Coach ID:', coachId);
        console.log('Coach Assignments - Is array:', Array.isArray(data));
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setError(e?.message || 'Failed to load assignments');
        setRows([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [coachId]);

  const sorted = useMemo(() => {
    const copy = [...(rows || [])];
    copy.sort((a, b) => {
      const ad = parseMmDdYyyy(a?.reportDate)?.getTime() ?? 0;
      const bd = parseMmDdYyyy(b?.reportDate)?.getTime() ?? 0;
      if (ad !== bd) return ad - bd;
      return String(a?.startTime || '').localeCompare(String(b?.startTime || ''));
    });
    return copy;
  }, [rows]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading assignments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assignments</h1>
          <p className="text-sm text-gray-600">
            Read-only view of assignments created by admin for <span className="font-semibold">{coachName}</span>.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <UserCheck size={16} />
          Assigned to you only
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-800">Your Assignments</div>
          <div className="text-xs text-gray-900">
            {sorted.length} assignment(s)
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {['Site', 'Address', 'Report date', 'Time', 'Role', 'Notes'].map((h) => (
                  <th key={h} className="text-left text-[11px] font-bold text-gray-500 uppercase tracking-wide px-4 py-2.5 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && sorted.length > 0 ? sorted.map((a, idx) => (
                <tr key={a.id || idx} className={idx < sorted.length - 1 ? 'border-b border-gray-100' : ''}>
                  <td className="px-4 py-3.5 align-top">
                    <div className="text-[13px] font-semibold text-gray-900">{a.siteName || '—'}</div>
                  </td>
                  <td className="px-4 py-3.5 align-top text-[13px] text-gray-700 min-w-[240px]">
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="mt-0.5 text-gray-400" />
                      <div className="leading-snug">{a.address || '—'}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 align-top">
                    <div className="text-[13px] font-semibold text-gray-900">{formatLongDate(a.reportDate)}</div>
                  </td>
                  <td className="px-4 py-3.5 align-top">
                    <div className="flex items-start gap-2">
                      <Clock size={16} className="mt-0.5 text-gray-400" />
                      <div className="leading-snug">{formatTime12h(a.startTime)} – {formatTime12h(a.endTime)}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 align-top">
                    <div className="text-[13px] text-gray-600">{a.role || '—'}</div>
                  </td>
                  <td className="px-4 py-3.5 align-top">
                    <div className="text-[13px] text-gray-400 whitespace-pre-wrap break-words max-w-[200px]">
                      {a.notes || '—'}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-500">
                    No assignments found for your account yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Assignments;

